import React, { useState, useEffect, useRef } from 'react';

export default function CompleteQubitVisualization() {
  const [isObserved, setIsObserved] = useState(false);
  const [result, setResult] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [theta, setTheta] = useState(Math.PI / 4); // 45 degrees
  const [phi, setPhi] = useState(0);
  const canvasRef = useRef(null);

  const alpha = Math.cos(theta / 2);
  const beta = Math.sin(theta / 2);
  const prob0 = Math.pow(alpha, 2) * 100;
  const prob1 = Math.pow(beta, 2) * 100;

  useEffect(() => {
    if (!isObserved) {
      const interval = setInterval(() => {
        setRotation(prev => (prev + 2) % 360);
      }, 50); // Reduced frequency for better performance
      return () => clearInterval(interval);
    }
  }, [isObserved]);

  // Draw Bloch sphere
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = 120;

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      // Draw sphere
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
      gradient.addColorStop(0, 'rgba(0, 120, 212, 0.10)');
      gradient.addColorStop(1, 'rgba(92, 45, 145, 0.04)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.fill();

      // Draw sphere outline
      ctx.strokeStyle = 'rgba(0, 120, 212, 0.5)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw axes
      ctx.strokeStyle = 'rgba(100, 100, 100, 0.5)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);

      // X axis
      ctx.beginPath();
      ctx.moveTo(centerX - radius, centerY);
      ctx.lineTo(centerX + radius, centerY);
      ctx.stroke();

      // Y axis (appears as horizontal due to perspective)
      ctx.beginPath();
      ctx.moveTo(centerX - radius * 0.7, centerY - 20);
      ctx.lineTo(centerX + radius * 0.7, centerY + 20);
      ctx.stroke();

      // Z axis
      ctx.beginPath();
      ctx.moveTo(centerX, centerY - radius);
      ctx.lineTo(centerX, centerY + radius);
      ctx.stroke();

      ctx.setLineDash([]);

      // Labels
      ctx.fillStyle = 'rgba(8, 145, 178, 1)';
      ctx.font = 'bold 18px sans-serif';
      ctx.fillText('|0⟩', centerX - 15, centerY - radius - 10);
      ctx.fillText('"Up"', centerX + 15, centerY - radius - 10);

      ctx.fillStyle = 'rgba(219, 39, 119, 1)';
      ctx.fillText('|1⟩', centerX - 15, centerY + radius + 25);
      ctx.fillText('"Down"', centerX + 15, centerY + radius + 25);

      ctx.fillStyle = 'rgba(90, 90, 90, 0.9)';
      ctx.font = 'bold 16px sans-serif';
      ctx.fillText('X', centerX + radius + 10, centerY + 5);
      ctx.fillText('Y', centerX - radius * 0.7 - 25, centerY - 25);
      ctx.fillText('Z', centerX + 10, centerY - radius - 5);

      if (!isObserved) {
        // Draw state vector
        const x = radius * Math.sin(theta) * Math.cos(phi + rotation * Math.PI / 180);
        const z = -radius * Math.cos(theta);

        // State vector arrow
        ctx.strokeStyle = 'rgba(217, 119, 6, 1)';
        ctx.fillStyle = 'rgba(217, 119, 6, 1)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(centerX + x, centerY + z);
        ctx.stroke();

        // Arrow head
        const angle = Math.atan2(z, x);
        const headLen = 10;
        ctx.beginPath();
        ctx.moveTo(centerX + x, centerY + z);
        ctx.lineTo(
          centerX + x - headLen * Math.cos(angle - Math.PI / 6),
          centerY + z - headLen * Math.sin(angle - Math.PI / 6)
        );
        ctx.moveTo(centerX + x, centerY + z);
        ctx.lineTo(
          centerX + x - headLen * Math.cos(angle + Math.PI / 6),
          centerY + z - headLen * Math.sin(angle + Math.PI / 6)
        );
        ctx.stroke();

        // |ψ⟩ label
        ctx.fillStyle = 'rgba(217, 119, 6, 1)';
        ctx.font = 'bold 20px sans-serif';
        ctx.fillText('|ψ⟩', centerX + x + 15, centerY + z - 5);

        // Draw angle θ
        ctx.strokeStyle = 'rgba(217, 119, 6, 0.5)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(centerX, centerY, 30, -Math.PI / 2, -Math.PI / 2 + theta, false);
        ctx.stroke();
        ctx.fillStyle = 'rgba(217, 119, 6, 0.8)';
        ctx.font = '16px sans-serif';
        ctx.fillText('θ', centerX + 35, centerY - 20);

        // Draw rotation circle
        ctx.strokeStyle = 'rgba(217, 119, 6, 0.3)';
        ctx.lineWidth = 1;
        ctx.setLineDash([2, 2]);
        ctx.beginPath();
        const circleRadius = radius * Math.sin(theta);
        const circleY = centerY - radius * Math.cos(theta);
        ctx.ellipse(centerX, circleY, circleRadius, circleRadius * 0.3, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
      } else {
        // Collapsed state
        const finalZ = result === 0 ? -radius : radius;
        ctx.strokeStyle = result === 0 ? 'rgba(8, 145, 178, 1)' : 'rgba(219, 39, 119, 1)';
        ctx.fillStyle = result === 0 ? 'rgba(8, 145, 178, 1)' : 'rgba(219, 39, 119, 1)';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(centerX, centerY + finalZ);
        ctx.stroke();

        // Arrow head
        const headLen = 12;
        ctx.beginPath();
        ctx.moveTo(centerX, centerY + finalZ);
        ctx.lineTo(centerX - headLen / 2, centerY + finalZ + (result === 0 ? headLen : -headLen));
        ctx.lineTo(centerX + headLen / 2, centerY + finalZ + (result === 0 ? headLen : -headLen));
        ctx.closePath();
        ctx.fill();
      }
    };

    draw();
  }, [theta, phi, rotation, isObserved, result]);

  const handleObserve = () => {
    const rand = Math.random();
    const outcome = rand < prob0 / 100 ? 0 : 1;
    setResult(outcome);
    setIsObserved(true);
  };

  const handleReset = () => {
    setIsObserved(false);
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Title */}
        <div className="text-center mb-4">
          <h1 className="text-4xl font-bold text-gray-900 mb-1">
            Qubit
          </h1>
          <p className="text-xl text-gray-600 mb-2">Quantum Bit</p>
          <p className="text-sm text-gray-500">
            {isObserved ? '⚡ Wavefunction Collapsed' : '🌊 Superposition State'}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
          {/* Left Column */}
          <div className="space-y-3">
            {/* Basis States */}
            <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
              <h2 className="text-base font-bold text-blue-700 mb-2 text-center">Basis States</h2>
              <div className="flex justify-around items-center">
                <div className="text-center">
                  <div className="text-cyan-600 text-xs mb-1">
                    <div className="border-2 border-cyan-500 rounded p-1 inline-block text-xs">
                      <div>0</div>
                      <div>1</div>
                    </div>
                  </div>
                  <div className="w-6 h-0.5 bg-cyan-500 mx-auto my-1"></div>
                  <div className="text-cyan-700 text-lg font-bold">0</div>
                </div>
                <div className="text-center">
                  <div className="text-pink-600 text-xs mb-1">
                    <div className="border-2 border-pink-500 rounded p-1 inline-block text-xs">
                      <div>1</div>
                      <div>0</div>
                    </div>
                  </div>
                  <div className="w-6 h-0.5 bg-pink-500 mx-auto my-1"></div>
                  <div className="text-pink-700 text-lg font-bold">1</div>
                </div>
              </div>
            </div>

            {/* Vector Form */}
            <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
              <h2 className="text-base font-bold text-purple-700 mb-2 text-center">Vector Form</h2>
              <div className="text-center space-y-1">
                <div className="text-gray-800 text-base">
                  |ψ⟩ =
                  <span className="inline-block border-l-2 border-r-2 border-purple-300 px-2 py-1 mx-2">
                    <div className="text-cyan-600 text-xs">α</div>
                    <div className="text-pink-600 text-xs">β</div>
                  </span>
                </div>
                <div className="text-gray-500 text-xs">
                  |ψ⟩ = cos(θ/2)|0⟩ + e<sup>iφ</sup>sin(θ/2)|1⟩
                </div>
              </div>
            </div>

            {/* Superposition State */}
            <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
              <h2 className="text-base font-bold text-amber-700 mb-2 text-center">Superposition State</h2>
              <div className="text-center space-y-1">
                <div className="text-gray-800 text-base">
                  |ψ⟩ = <span className="text-cyan-600">α</span>|0⟩ + <span className="text-pink-600">β</span>|1⟩
                </div>
                <div className="text-cyan-700 text-xs">
                  α: Probability of 0 = |α|²
                </div>
                <div className="text-pink-700 text-xs">
                  β: Probability of 1 = |β|²
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="bg-cyan-50 rounded-lg p-2 relative border border-cyan-100">
                    <div className="text-cyan-600 text-xs">P(|0⟩)</div>
                    <div className="text-cyan-700 text-base font-bold">{prob0.toFixed(1)}%</div>
                    <div className="absolute bottom-0 left-0 h-1 bg-cyan-500 rounded-b-lg transition-all duration-300"
                         style={{width: `${prob0}%`}}></div>
                  </div>
                  <div className="bg-pink-50 rounded-lg p-2 relative border border-pink-100">
                    <div className="text-pink-600 text-xs">P(|1⟩)</div>
                    <div className="text-pink-700 text-base font-bold">{prob1.toFixed(1)}%</div>
                    <div className="absolute bottom-0 left-0 h-1 bg-pink-500 rounded-b-lg transition-all duration-300"
                         style={{width: `${prob1}%`}}></div>
                  </div>
                </div>

                {/* Amplitude Display */}
                <div className="mt-2 text-xs text-gray-600">
                  <div>α = {alpha.toFixed(3)} (amplitude for |0⟩)</div>
                  <div>β = {beta.toFixed(3)} (amplitude for |1⟩)</div>
                  <div className="text-amber-600 mt-1">|α|² + |β|² = {(prob0/100 + prob1/100).toFixed(3)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Bloch Sphere */}
          <div className="space-y-3">
            <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
              <h2 className="text-base font-bold text-purple-700 mb-2 text-center">Bloch Sphere</h2>
              <div className="flex justify-center">
                <canvas ref={canvasRef} width={250} height={250} className="rounded-lg" />
              </div>
            </div>

            {/* Controls */}
            {!isObserved && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                <h3 className="text-gray-800 font-semibold mb-2 text-center text-sm">Adjust Superposition</h3>
                <div className="space-y-2">
                  <div>
                    <label className="text-gray-600 text-xs block mb-1">
                      Angle θ: {(theta * 180 / Math.PI).toFixed(1)}°
                    </label>
                    <input
                      type="range"
                      min="0"
                      max={Math.PI}
                      step="0.01"
                      value={theta}
                      onChange={(e) => setTheta(parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>0°</span>
                      <span>90°</span>
                      <span>180°</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs block mb-1">
                      Phase φ: {(phi * 180 / Math.PI).toFixed(1)}°
                    </label>
                    <input
                      type="range"
                      min="0"
                      max={Math.PI * 2}
                      step="0.01"
                      value={phi}
                      onChange={(e) => setPhi(parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>0°</span>
                      <span>180°</span>
                      <span>360°</span>
                    </div>
                  </div>

                  {/* Preset Buttons */}
                  <div className="mt-3 pt-2 border-t border-gray-200">
                    <div className="text-xs text-gray-500 mb-2 text-center">Quick Presets:</div>
                    <div className="grid grid-cols-2 gap-1">
                      <button
                        onClick={() => {setTheta(0); setPhi(0);}}
                        className="px-2 py-1 bg-cyan-50 text-cyan-700 border border-cyan-200 text-xs rounded hover:bg-cyan-100 transition-all"
                      >
                        |0⟩ State
                      </button>
                      <button
                        onClick={() => {setTheta(Math.PI); setPhi(0);}}
                        className="px-2 py-1 bg-pink-50 text-pink-700 border border-pink-200 text-xs rounded hover:bg-pink-100 transition-all"
                      >
                        |1⟩ State
                      </button>
                      <button
                        onClick={() => {setTheta(Math.PI/2); setPhi(0);}}
                        className="px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 text-xs rounded hover:bg-amber-100 transition-all"
                      >
                        |+⟩ State
                      </button>
                      <button
                        onClick={() => {setTheta(Math.PI/2); setPhi(Math.PI);}}
                        className="px-2 py-1 bg-orange-50 text-orange-700 border border-orange-200 text-xs rounded hover:bg-orange-100 transition-all"
                      >
                        |-⟩ State
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Result Display */}
            {isObserved && (
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                <h3 className="text-gray-800 font-bold text-base mb-2 text-center">Measurement Result</h3>
                <div className="text-center">
                  <div className={`text-4xl font-bold ${result === 0 ? 'text-cyan-600' : 'text-pink-600'} mb-1`}>
                    {result}
                  </div>
                  <div className="text-gray-500 text-xs">
                    Collapsed to state |{result}⟩
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center gap-3">
          {!isObserved ? (
            <>
              <button
                onClick={handleObserve}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-xl shadow-md transform hover:scale-105 transition-all duration-200"
              >
                🔍 Observe Qubit
              </button>
              <button
                onClick={() => {setTheta(Math.PI/4); setPhi(0);}}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white font-bold text-sm rounded-xl shadow-md transform hover:scale-105 transition-all duration-200"
              >
                ↻ Reset
              </button>
            </>
          ) : (
            <button
              onClick={handleReset}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-xl shadow-md transform hover:scale-105 transition-all duration-200"
            >
              ↻ Reset to Superposition
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
